/**
 * SyncManager - Cross-Platform Offline-First Sync with Supabase
 *
 * Handles bi-directional synchronization between local database and remote Supabase backend.
 * Uses the platform adapter pattern for cross-platform compatibility (web and mobile).
 *
 * Features:
 * - Initial data load
 * - Pending operations queue
 * - Incremental sync
 * - Network status detection
 * - Repository pattern for data access
 */

import { SupabaseClient } from '@supabase/supabase-js';
import { getRepositories, isFactoryReady, repositoryEvents, RepositoryChangeEvent } from '../services/storage';
import { supabase } from './supabaseClient';
import { Animal, AnimalHistorico } from '../types/interfaces';
import { TABLE_CONFIG } from '../configs/tableConfig';
import type { PendingChange } from '../configs/tableConfig';
import { ISyncPlatformAdapter } from '../services/storage/interfaces/ISyncPlatformAdapter';
import { TableRealtimeSync } from './TableRealtimeSync';
import { pullTables, PullTableConfig } from './syncPull';
import { flushPendingBatch, BatchSenders, MAX_PUSH_RETRIES, tablasPadreDeSync, esPendienteRetenido } from './syncBatcher';
import { acquireSyncLock, releaseSyncLock, SYNC_LOCK_KEY } from './syncLock';
import type { MetaTable } from './syncLock';
import { stripLocalFields as stripLocalFieldsShared } from './syncFields';
import { esErrorPermisos, mensajeRechazadoPermisos } from './syncErrors';
import type { ConceptoRelacionRepository } from '../services/storage/repositories/ConceptoRepository';

/**
 * Interface for syncable repository operations
 */
interface ISyncableRepository {
  get(id: string): Promise<any>;
  getAll(): Promise<any[]>;
  delete(id: string): Promise<void>;
  update(data: any): Promise<void>;
  count(): Promise<number>;
  clear(): Promise<void>;
  bulkPutSilent(items: any[]): Promise<void>;
  bulkDeleteSilent(ids: string[]): Promise<void>;
  getLastByDate(dateColumn?: string): Promise<any>;
}

/**
 * Names of syncable tables
 * Note: 'stocks' and 'animales' are local derived tables that don't sync to Supabase
 * but are included here so SyncManager can access them for rebuild operations
 */
type SyncableTable =
  | 'animales'
  | 'animales_historico'
  | 'stocks'
  | 'stocks_historico'
  | 'gastos'
  | 'ingresos'
  | 'notas'
  | 'eventos'
  | 'recordatorios'
  | 'lluvias'
  | 'elementos_mapa'
  | 'puntos_control'
  | 'puntos_control_eventos'
  | 'siembras_lote'
  | 'nota_anclas'
  | 'personas_campo'
  | 'sigsa_declaraciones'
  | 'sync_acciones'
  | 'ubicaciones'
  | 'unidades'
  | 'empresas'
  | 'profiles'
  | 'operacion_compra_venta_animales';

/**
 * Tamaño máximo de chunk para operaciones bulk en Supabase.
 * Evita exceder el límite de payload de una sola request HTTP.
 */
const BULK_BATCH_SIZE = 500;

/**
 * TTL del lock de sincronización compartido entre app y Service Worker.
 * Coincide con el timeout general de sync (2 min) para auto-liberar si un
 * contexto crashea manteniendo el lock.
 */
const SYNC_LOCK_TTL_MS = 120000;

// ======================================================================
// Event Emitter Types
// ======================================================================

/**
 * Sync event types for UI notifications
 */
export type SyncEventType = 'sync_started' | 'sync_completed' | 'sync_error' | 'network_status' | 'initial_load' | 'sync_pending';

/**
 * Sync phases
 */
export type SyncPhase = 'push' | 'pull' | 'initial_load' | 'company_switch';

/**
 * Sync event structure
 */
export interface SyncEvent {
  type: SyncEventType;
  phase?: SyncPhase;
  isOnline?: boolean;
  error?: string;
  count?: number;
}

/**
 * Callback type for sync events
 */
export type SyncEventCallback = (event: SyncEvent) => void;

// ======================================================================
// SyncManager - Cross-Platform Implementation
// ======================================================================

export class SyncManager {
  private supabase: SupabaseClient;
  private initialized: boolean;
  private syncTimer: ReturnType<typeof setTimeout> | null;
  /** Pushes inmediatos en curso por tabla (para que las hijas esperen a su padre). */
  private pushesEnVuelo = new Map<string, Set<Promise<void>>>();
  private platformAdapter: ISyncPlatformAdapter | null;
  private networkUnsubscribe: (() => void) | null;
  private repositoryEventsUnsubscribe: (() => void) | null;
  private listeners: Set<SyncEventCallback>;
  private isSyncing: boolean;
  private syncLock: Promise<void> | null; // Promise-based lock to prevent concurrent syncs
  private tableRealtimeSync: TableRealtimeSync | null = null;
  // Listener de visibilidad para pausar el sync periódico cuando la pestaña
  // está oculta y hacer catch-up al volver a estar visible.
  private onVisibilityChange: (() => void) | null = null;

  constructor() {
    this.supabase = supabase;
    this.initialized = false;
    this.syncTimer = null;
    this.platformAdapter = null;
    this.networkUnsubscribe = null;
    this.repositoryEventsUnsubscribe = null;
    this.listeners = new Set();
    this.isSyncing = false;
    this.syncLock = null;
  }

  // ==================== Event Emitter Pattern ====================

  /**
   * Add a listener for sync events
   * @param callback - Function to call when sync events occur
   * @returns Unsubscribe function
   */
  addListener(callback: SyncEventCallback): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Remove a listener
   */
  removeListener(callback: SyncEventCallback): void {
    this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of an event
   */
  private notifyListeners(event: SyncEvent): void {
    this.listeners.forEach(callback => {
      try {
        callback(event);
      } catch (err) {
        console.error('[SyncManager] Listener error:', err);
      }
    });
  }

  // ==================== Helper Methods ====================

  /**
   * Remove local-only fields from a record before syncing to Supabase.
   * Delega en el módulo compartido `syncFields` (usado también por el SW).
   * - sync_status: Local-only sync tracking (all tables)
   * - is_deleted: Only for tables that DON'T have this column in Supabase
   */
  private stripLocalFields(record: Record<string, any>, table?: string): Record<string, any> {
    return stripLocalFieldsShared(record, table);
  }

  // ==================== Public Status Methods ====================

  /**
   * Check if currently online
   */
  getIsOnline(): boolean {
    if (!this.platformAdapter) {
      return true; // Default to online if adapter not set
    }
    return this.platformAdapter.isOnline();
  }

  /**
   * Alias for getIsOnline() for convenience
   */
  isOnline(): boolean {
    return this.getIsOnline();
  }

  /**
   * Check if currently syncing
   */
  getIsSyncing(): boolean {
    return this.isSyncing;
  }

  /**
   * Force reset the syncing state (recovery method)
   * Use this if sync gets stuck due to crashed app or timeout
   */
  forceResetSyncState(): void {
    if (this.isSyncing) {
      console.warn('[SyncManager] ⚠️ Sync was stuck - resetting state (this can happen after app crash during sync)');
      this.isSyncing = false;
      this.notifyListeners({ type: 'sync_error', error: 'Sync was reset due to timeout or crash' });
    }
  }

  /**
   * Get current sync status
   */
  async getStatus(): Promise<{
    isSyncing: boolean;
    isOnline: boolean;
    pendingCount: number;
    lastSync: string | null;
  }> {
    const pendingCount = await this.getPendingCount();
    const lastSync = await this.getLastSyncTime();

    return {
      isSyncing: this.isSyncing,
      isOnline: this.getIsOnline(),
      pendingCount,
      lastSync
    };
  }

  /**
   * Get count of pending changes
   */
  async getPendingCount(): Promise<number> {
    try {
      const adapter = this.getAdapter();
      return await adapter.getPendingChangesCount();
    } catch {
      return 0;
    }
  }

  /**
   * Set the platform adapter (must be called before using sync operations)
   * This is typically called by the platform-specific initialization code
   */
  setPlatformAdapter(adapter: ISyncPlatformAdapter): void {
    this.platformAdapter = adapter;

    // Set up network change listener
    if (this.networkUnsubscribe) {
      this.networkUnsubscribe();
    }
    this.networkUnsubscribe = adapter.onNetworkChange((online) => {
      console.log(`[SyncManager] Network status changed: ${online ? 'online' : 'offline'}`);

      // Notify listeners of network status change
      this.notifyListeners({
        type: 'network_status',
        isOnline: online
      });

      // Notify AnimalRealtimeSync of network change
      this.notifyNetworkChangeToRealtime(online);

      if (online) {
        this.syncPending();
      }
    });

    // Subscribe to repository change events
    // This replaces direct sync.saveRemote() calls in repositories
    if (this.repositoryEventsUnsubscribe) {
      this.repositoryEventsUnsubscribe();
    }
    if (repositoryEvents && typeof repositoryEvents.subscribe === 'function') {
      this.repositoryEventsUnsubscribe = repositoryEvents.subscribe((event: RepositoryChangeEvent) => {
        this.despacharCambioRepositorio(event);
      });
      console.log('[SyncManager] Subscribed to repository change events');
    } else {
      console.error('[SyncManager] ERROR: repositoryEvents is not available - repository change events will NOT be synced!');
      console.error('[SyncManager] repositoryEvents value:', repositoryEvents);
    }

    console.log(`[SyncManager] Platform adapter set: ${adapter.platform}`);

    // Exponer el SyncManager en window (sólo dev) para que los tests E2E puedan
    // esperar a que esté suscripto antes de crear/editar (evita la race en la que
    // el evento del create se emite antes de la suscripción y se pierde) y forzar
    // un flush de la cola. No se expone en producción.
    if (process.env.NODE_ENV !== 'production') {
      try {
        (window as any).__mcdSync = this;
        (window as any).__mcdSyncReady = true;
      } catch {
        /* window puede no existir (SSR/tests unitarios) */
      }
    }
  }

  /**
   * Despacha un evento de repositorio respetando DEPENDENCIAS_SYNC (syncBatcher).
   *
   * El emitter es síncrono y cada push inmediato corre sin esperar al anterior:
   * sin esto, crear un lote de siembra (INSERT elementos_mapa) y su siembra
   * (INSERT siembras_lote) salían como dos requests concurrentes y la hija podía
   * llegar antes que su padre (RLS 42501 / FK). El registro en `pushesEnVuelo`
   * se hace de forma SÍNCRONA (antes de cualquier await) para que el evento hijo
   * emitido a continuación ya vea al padre en vuelo. Las tablas sin padre
   * declarado no esperan a nadie (mismo paralelismo que antes).
   */
  private despacharCambioRepositorio(event: RepositoryChangeEvent): void {
    const padres = tablasPadreDeSync(event.table);
    const esperar = padres.flatMap(t => Array.from(this.pushesEnVuelo.get(t) ?? []));
    const push = (async () => {
      if (esperar.length > 0) await Promise.allSettled(esperar);
      await this.handleRepositoryChange(event, padres);
    })().catch(error => console.error('[SyncManager] Failed to handle repository change:', error));

    let enVuelo = this.pushesEnVuelo.get(event.table);
    if (!enVuelo) {
      enVuelo = new Set();
      this.pushesEnVuelo.set(event.table, enVuelo);
    }
    enVuelo.add(push);
    push.finally(() => enVuelo!.delete(push));
  }

  /**
   * Handle repository change events from the event emitter
   * Automatically syncs changes to the remote backend
   *
   * @param padres tablas padre declaradas (DEPENDENCIAS_SYNC). Si alguna tiene
   *        pendientes en la cola (offline o push fallido), este cambio se encola
   *        detrás en vez de mandarse solo: el batcher los envía padre-primero.
   */
  private async handleRepositoryChange(event: RepositoryChangeEvent, padres: string[] = []): Promise<void> {
    const recordInfo = (() => {
      if (typeof event.record === 'string') return { id: event.record };
      if (Array.isArray(event.record)) {
        // BULK_INSERT/BULK_UPDATE -> array de registros; BULK_DELETE -> array de ids
        const count = event.record.length;
        const first = event.record[0];
        const id = typeof first === 'string' ? first : (first as any)?.id;
        return { count, id };
      }
      return { id: (event.record as any).id, title: (event.record as any).title };
    })();
    console.log(`[SyncManager] Repository change detected: ${event.action} on ${event.table}`, recordInfo);

    // If offline, queue the change for later sync
    if (!this.getIsOnline()) {
      console.log('[SyncManager] Offline - queuing change for later sync', { table: event.table, action: event.action, ...recordInfo });
      const adapter = this.getAdapter();
      await adapter.addPendingChange({
        table: event.table,
        action: event.action,
        record: event.record
      });
      this.notifyListeners({ type: 'sync_pending', count: 1 });
      return;
    }

    // Hija con su padre todavía en la cola: encolar detrás (no mandarla sola).
    if (padres.length > 0) {
      const adapter = this.getAdapter();
      // Sólo cuentan los padres que todavía se van a enviar: uno retenido
      // (CONFLICTO / RECHAZADO) no sube nunca y no debe frenar a la hija.
      const padreEnCola = (await adapter.getPendingChanges()).some(
        c => padres.includes(c.table) && !esPendienteRetenido(c),
      );
      if (padreEnCola) {
        console.log(`[SyncManager] ${event.table}: su tabla padre tiene pendientes en la cola, se encola detrás`, recordInfo);
        await adapter.addPendingChange({ table: event.table, action: event.action, record: event.record });
        await this.registerBackgroundSync();
        this.notifyListeners({ type: 'sync_pending', count: 1 });
        return;
      }
    }

    // If online, sync immediately
    try {
      console.log(`[SyncManager] Syncing immediately: ${event.action} on ${event.table}`, recordInfo);
      await this.saveRemote(event.record, event.table, event.action);
      console.log(`[SyncManager] ✅ Sync complete: ${event.action} on ${event.table}`, recordInfo);
    } catch (error) {
      console.error('[SyncManager] Failed to sync change:', error);
    }
  }

  /**
   * Get the platform adapter (throws if not set)
   */
  private getAdapter(): ISyncPlatformAdapter {
    if (!this.platformAdapter) {
      throw new Error('[SyncManager] Platform adapter not set. Call setPlatformAdapter() first.');
    }
    return this.platformAdapter;
  }

  /**
   * Get a repository by table name
   */
  private getRepo(tableName: SyncableTable): ISyncableRepository | null {
    try {
      if (!isFactoryReady()) {
        return null;
      }
      const repos = getRepositories();

      const repoMap: Record<string, ISyncableRepository> = {
        animales: repos.animal as unknown as ISyncableRepository,
        animales_historico: repos.animalHistorico as unknown as ISyncableRepository,
        stocks: repos.stock as unknown as ISyncableRepository,
        stocks_historico: repos.stockHistorico as unknown as ISyncableRepository,
        gastos: repos.gasto as unknown as ISyncableRepository,
        ingresos: repos.ingreso as unknown as ISyncableRepository,
        notas: repos.nota as unknown as ISyncableRepository,
        eventos: repos.evento as unknown as ISyncableRepository,
        recordatorios: repos.recordatorio as unknown as ISyncableRepository,
        lluvias: repos.lluvia as unknown as ISyncableRepository,
        elementos_mapa: repos.elementoMapa as unknown as ISyncableRepository,
        puntos_control: repos.puntoControl as unknown as ISyncableRepository,
        puntos_control_eventos: repos.puntoControlEvento as unknown as ISyncableRepository,
        siembras_lote: repos.siembraLote as unknown as ISyncableRepository,
        nota_anclas: repos.notaAncla as unknown as ISyncableRepository,
        personas_campo: repos.personaCampo as unknown as ISyncableRepository,
        sigsa_declaraciones: repos.sigsaDeclaracion as unknown as ISyncableRepository,
        sync_acciones: repos.syncAcciones as unknown as ISyncableRepository,
        ubicaciones: repos.ubicacion as unknown as ISyncableRepository,
        unidades: repos.unidad as unknown as ISyncableRepository,
        empresas: repos.empresa as unknown as ISyncableRepository,
        profiles: repos.profile as unknown as ISyncableRepository,
        operacion_compra_venta_animales: repos.operacion as unknown as ISyncableRepository,
      };

      return repoMap[tableName] || null;
    } catch {
      return null;
    }
  }

  /**
   * Check if a repository exists
   */
  private checkRepoExists(tableName: SyncableTable): boolean {
    return this.getRepo(tableName) !== null;
  }

  /**
   * @deprecated Use checkRepoExists instead
   */
  async checkTableExists(tableName: string): Promise<boolean> {
    return this.checkRepoExists(tableName as SyncableTable);
  }

  /**
   * Get the last sync timestamp
   */
  async getLastSyncTime(): Promise<string> {
    try {
      const adapter = this.getAdapter();
      const value = await adapter.getSyncMetadata('lastSyncTime');
      return value || new Date(0).toISOString();
    } catch (e) {
      console.error('Error getting last sync time:', e);
      return new Date(0).toISOString();
    }
  }

  /**
   * Update the last sync timestamp
   */
  private async setLastSyncTime(synctime: string = new Date().toISOString()): Promise<void> {
    try {
      const adapter = this.getAdapter();
      await adapter.setSyncMetadata('lastSyncTime', synctime);
    } catch (e) {
      console.error('Error setting last sync time:', e);
    }
  }

  async saveRemote(record: Record<string, any> | string | Record<string, any>[], table: string, action: 'INSERT' | 'UPDATE' | 'DELETE' | 'BULK_INSERT' | 'BULK_DELETE' | 'BULK_UPDATE' = "INSERT"): Promise<void> {
    const adapter = this.getAdapter();

    console.log(`[SyncManager] saveRemote: ${action} on ${table}`, record);

    try {
      // ==================== BULK INSERT ====================
      // Recibe un array de registros y los envía a Supabase en una sola operación
      // (con chunking para no exceder límites de payload).
      if (action === "BULK_INSERT") {
        const records = record as Record<string, any>[];
        if (!Array.isArray(records) || records.length === 0) return;

        // Limpiar campos locales de cada registro
        const cleanRecords = records.map(r => this.stripLocalFields(r, table));

        // Chunking para arrays grandes (límite práctico de payload de Supabase)
        const chunks = this.chunkArray(cleanRecords, BULK_BATCH_SIZE);

        for (const chunk of chunks) {
          console.log(`[SyncManager] Bulk upserting ${chunk.length} records to ${table}`);
          const { error } = await this.supabase
            .from(table)
            .upsert(chunk);

          if (error) {
            if (error.message === "TypeError: Failed to fetch") {
              console.log('Network error. Will retry via sync queue.', error.message);
              // Encolar el chunk que falló como pending change bulk
              await adapter.addPendingChange({ table, action: 'BULK_INSERT', record: chunk });
              await this.registerBackgroundSync();
              continue;
            }
            // OTRO error (RLS, uniqueness, 5xx...): el chunk NO se descarta —
            // se re-encola para reintentar/declarar conflicto sin perder datos.
            console.error(`[SyncManager] ❌ Supabase bulk upsert error on ${table}:`, error.message, error);
            console.error('[SyncManager] Chunk that failed (first record):', chunk[0]);
            await this.requeueFailedPush({ table, action: 'BULK_INSERT', record: chunk }, error.message);
            continue;
          }
          console.log(`[SyncManager] ✅ Bulk upserted ${chunk.length} records to ${table}`);
        }
        return;
      }

      // ==================== BULK DELETE ====================
      // Recibe un array de IDs (o un id suelto) y los elimina en una sola operación.
      if (action === "BULK_DELETE") {
        const ids = Array.isArray(record)
          ? (record as string[])
          : [record as string];
        if (ids.length === 0) return;

        const pk = TABLE_CONFIG[table]?.primaryKey || 'id';
        console.log(`[SyncManager] Bulk deleting ${ids.length} records from ${table}`);
        const { error } = await this.supabase
          .from(table)
          .delete()
          .in(pk, ids);

        if (error) {
          if (error.message === "TypeError: Failed to fetch") {
            console.log('Network error. Will retry via sync queue.', error.message);
            await adapter.addPendingChange({ table, action: 'BULK_DELETE', record: ids });
            await this.registerBackgroundSync();
            return;
          }
          console.error(`[SyncManager] ❌ Supabase bulk delete error on ${table}:`, error.message, error);
          await this.requeueFailedPush({ table, action: 'BULK_DELETE', record: ids }, error.message);
          return;
        }
        console.log(`[SyncManager] ✅ Bulk deleted ${ids.length} records from ${table}`);
        return;
      }

      // ==================== BULK UPDATE ====================
      // Recibe un array de registros parciales. Usamos upsert con merge
      // (onConflict=pk + defaultToNull:false) para que solo se actualicen las
      // columnas enviadas y las demás queden intactas, en una sola operación.
      if (action === "BULK_UPDATE") {
        const records = Array.isArray(record) ? record as Record<string, any>[] : [record as Record<string, any>];
        if (records.length === 0) return;

        const pk = TABLE_CONFIG[table]?.primaryKey || 'id';
        const cleanRecords = records
          .map(r => this.stripLocalFields(r, table))
          .filter(r => r[pk] !== undefined && r[pk] !== null);

        if (cleanRecords.length === 0) return;

        // Chunking para arrays grandes (límite práctico de payload de Supabase),
        // igual que BULK_INSERT. El SW ya chunkea este camino (sw.ts + syncBatcher).
        const chunks = this.chunkArray(cleanRecords, BULK_BATCH_SIZE);

        for (const chunk of chunks) {
          console.log(`[SyncManager] Bulk updating (merge) ${chunk.length} records on ${table}`);
          const { error } = await this.supabase
            .from(table)
            .upsert(chunk, { onConflict: pk, defaultToNull: false });

          if (error) {
            if (error.message === "TypeError: Failed to fetch") {
              console.log('Network error. Will retry via sync queue.', error.message);
              // Encolar el chunk que falló como pending change bulk
              await adapter.addPendingChange({ table, action: 'BULK_UPDATE', record: chunk });
              await this.registerBackgroundSync();
              continue;
            }
            // OTRO error (RLS, uniqueness, 5xx...): el chunk NO se descarta —
            // se re-encola para reintentar/declarar conflicto sin perder datos.
            console.error(`[SyncManager] ❌ Supabase bulk update error on ${table}:`, error.message, error);
            console.error('[SyncManager] Chunk that failed (first record):', chunk[0]);
            await this.requeueFailedPush({ table, action: 'BULK_UPDATE', record: chunk }, error.message);
            continue;
          }
          console.log(`[SyncManager] ✅ Bulk updated ${chunk.length} records on ${table}`);
        }
        return;
      }

      // ==================== Single record operations ====================
      // Strip local-only fields (sync_status, is_deleted for some tables)
      const cleanRecord: Record<string, any> | string = typeof record !== 'string'
        ? this.stripLocalFields(record, table)
        : record;

      if (action === "INSERT") {
        console.log(`[SyncManager] Upserting to Supabase: ${table}`, cleanRecord);
        const { error } = await this.supabase
          .from(table)
          .upsert(cleanRecord as Record<string, any>);

        if (error) {
          if (error.message === "TypeError: Failed to fetch") {
            console.log('Network error. Will retry via sync queue.', error.message);
            await adapter.addPendingChange({ table, action, record });
            await this.registerBackgroundSync();
            return;
          }
          console.error(`[SyncManager] ❌ Supabase upsert error on ${table}:`, error.message, error);
          console.error('[SyncManager] Record that failed:', cleanRecord);
          await this.requeueFailedPush({ table, action, record }, error.message);
          return;
        }
        console.log(`[SyncManager] ✅ Upserted to Supabase: ${table}`, record);
      }
      else if (action === "UPDATE") {
        console.log(`[SyncManager] Updating in Supabase: ${table}`, cleanRecord);
        const { error } = await this.supabase
          .from(table)
          .update(cleanRecord as Record<string, any>)
          .eq(TABLE_CONFIG[table].primaryKey, (cleanRecord as Record<string, any>)[TABLE_CONFIG[table].primaryKey]);

        if (error) {
          if (error.message === "TypeError: Failed to fetch") {
            console.log('Network error. Will retry via sync queue.', error.message);
            await adapter.addPendingChange({ table, action, record });
            await this.registerBackgroundSync();
            return;
          }
          console.error(`[SyncManager] ❌ Supabase update error on ${table}:`, error.message, error);
          console.error('[SyncManager] Record that failed:', cleanRecord);
          await this.requeueFailedPush({ table, action, record }, error.message);
          return;
        }
        console.log(`[SyncManager] ✅ Updated in Supabase: ${table}`, record);
      }
      else if (action === "DELETE") {
        // record puede ser: string (id), objeto, o array (ids u objetos).
        // Si es un array, redirigir a delete masivo por .in(pk, ids).
        if (Array.isArray(record)) {
          const pk = TABLE_CONFIG[table]?.primaryKey || 'id';
          const ids = record
            .map((item) => typeof item === 'string' ? item : (item as Record<string, any>)?.[pk])
            .filter((id): id is string => typeof id === 'string');
          if (ids.length === 0) return;
          console.log(`[SyncManager] Deleting ${ids.length} records from ${table} (array routed to bulk)`);
          const { error } = await this.supabase
            .from(table)
            .delete()
            .in(pk, ids);
          if (error) {
            if (error.message === "TypeError: Failed to fetch") {
              console.log('Network error. Will retry via sync queue.', error.message);
              await adapter.addPendingChange({ table, action: 'BULK_DELETE', record: ids });
              await this.registerBackgroundSync();
              return;
            }
            await this.requeueFailedPush({ table, action: 'BULK_DELETE', record: ids }, error.message);
            alert(error.message);
            return;
          }
          console.log(`[SyncManager] ✅ Deleted ${ids.length} records from ${table}`);
          return;
        }

        const pkValue = typeof record === 'string' ? record : (record as Record<string, any>)[TABLE_CONFIG[table].primaryKey];
        const { error } = await this.supabase
          .from(table)
          .delete()
          .eq(TABLE_CONFIG[table].primaryKey, pkValue);

        if (error) {
          if (error.message === "TypeError: Failed to fetch") {
            console.log('Network error. Will retry via sync queue.', error.message);
            await adapter.addPendingChange({ table, action, record });
            await this.registerBackgroundSync();
            return;
          }
          await this.requeueFailedPush({ table, action, record }, error.message);
          alert(error.message);
          return;
        }
        console.log('Delete saved to Supabase!');
      }
    } catch (error: any) {
      // El envío lanzó (en vez de devolver {error}): mismo contrato — el
      // cambio se re-encola, no se pierde.
      await this.requeueFailedPush(
        { table, action, record },
        error?.message || String(error),
      );
      alert(error.message);
    }
  }

  /**
   * Re-encola un cambio cuyo push directo a Supabase falló con un error que
   * NO es de red (RLS, uniqueness de `conceptos_unicos`, 5xx, etc).
   *
   * Antes estos errores solo se logueaban y el cambio se descartaba: el
   * registro no quedaba ni en el server ni en la cola de pendientes, y el
   * próximo PULL lo borraba del caché local (pérdida de datos silenciosa).
   * Ahora el cambio sobrevive en `pending_changes`: el Background Sync lo
   * reintenta hasta agotar MAX_PUSH_RETRIES y después queda retenido como
   * CONFLICTO (nunca se borra) para que el usuario lo resuelva (ej:
   * renombrar un concepto duplicado).
   *
   * Nace con retryCount=1 + lastError (este push ya fue su primer intento
   * fallido) vía markPendingChange: addPendingChange no persiste retryCount/
   * lastError en todos los adapters (SQLite los ignora), así que el marcado
   * es lo que hace efectivo el contador en ambas plataformas.
   */
  private async requeueFailedPush(
    change: Pick<PendingChange, 'table' | 'action' | 'record'>,
    errorMessage: string,
  ): Promise<void> {
    const adapter = this.getAdapter();
    try {
      const id = await adapter.addPendingChange(change);
      if (adapter.markPendingChange && typeof id === 'number') {
        await adapter.markPendingChange(id, `[PUSH-FAILED] ${errorMessage}`);
      }
      await this.registerBackgroundSync();
      console.error(
        `[SyncManager] [PUSH-FAILED] ${change.action} en ${change.table} re-encolado como pending #${id} (reintenta hasta marcarse CONFLICTO; no se pierde):`,
        errorMessage,
      );
    } catch (e) {
      // Última línea de defensa: si ni siquiera se puede encolar, dejar el
      // rastro (el dato sigue vivo en el caché local del repo).
      console.error(
        `[SyncManager] [PUSH-FAILED] CRÍTICO: no se pudo re-encolar ${change.action} en ${change.table}:`,
        e,
      );
    }
  }

  /**
   * Register background sync (platform-specific)
   */
  private async registerBackgroundSync(): Promise<void> {
    const adapter = this.getAdapter();
    if (adapter.registerBackgroundSync) {
      await adapter.registerBackgroundSync();
    }
  }

  /**
   * Sync pending changes
   */
  async syncPending(): Promise<void> {
    const adapter = this.getAdapter();

    // Check if online first (before waiting for lock)
    if (!adapter.isOnline()) {
      console.log('[SyncManager] Cannot sync: offline');
      return;
    }
    // Sin sesión válida → no pushear (RLS rechazaría / la query iría como anon).
    if (!(await this.hasValidSessionForRemote())) {
      console.log('[SyncManager] Cannot sync: no valid session');
      return;
    }

    // Wait for any existing sync to complete, then start a new one
    // This prevents concurrent syncs that cause SQLite transaction errors
    if (this.syncLock) {
      console.log('[SyncManager] Waiting for existing sync to complete...');
      try {
        await this.syncLock;
      } catch (error) {
        // Previous sync failed, but we can try a new one
        console.log('[SyncManager] Previous sync completed with error, starting new sync');
      }
    }

    // Create a new lock for this sync operation
    let resolveLock: (() => void) | undefined;
    this.syncLock = new Promise<void>(resolve => { resolveLock = resolve; });

    this.isSyncing = true;
    this.notifyListeners({ type: 'sync_started', phase: 'push' });

    // Add timeout protection - if sync takes too long, reset and notify error
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Sync timeout after 2 minutes')), 120000);
    });

    try {
      // Race the sync against a timeout to prevent infinite hangs
      await Promise.race([
        this._doSyncPending(),
        timeoutPromise
      ]);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error("Error during sync", error);
      this.notifyListeners({ type: 'sync_error', error: errorMessage });
    } finally {
      this.isSyncing = false;
      // Release the lock so other syncs can proceed
      if (resolveLock) {
        resolveLock();
      }
      // Clear the lock reference after a short delay to allow waiting syncs to proceed
      setTimeout(() => {
        if (this.syncLock && this.syncLock !== Promise.resolve()) {
          this.syncLock = null;
        }
      }, 100);
    }
  }

  /**
   * Internal method that performs the actual sync operations
   *
   * Dos fases:
   *  - PULL: trae del server los cambios de otros usuarios. Por tabla, se
   *    ejecuta SOLO si realtime NO la cubre (isCoveredByRealtime). Cuando el
   *    canal `empresa:{id}` escucha y la tabla está registrada, los cambios
   *    ajenos llegan instantáneamente por broadcast y este PULL es redundante.
   *    Queda como fallback por tabla: arranque, brecha, canal caído (ERROR) o
   *    tablas sin realtime (ej: `empresas`, que no es empresa-scoped).
   *  - PUSH: drena la cola local `pending_changes` hacia Supabase. SIEMPRE se
   *    ejecuta: realtime es solo server→cliente (PULL), no envía cambios
   *    locales. Sin esto, lo que escribe el usuario no llegaría al server.
   */
  private async _doSyncPending(): Promise<void> {

      // PULL por tabla (fallback si realtime no la cubre). En la carga inicial
      // init() llama a pullAllTables(true) para forzar el PULL sin importar
      // realtime (el catch-up de realtime no es fuente confiable para la carga
      // inicial que la UI y los tests esperan disponibles al arrancar).
      await this.pullAllTables(false);

      // PUSH siempre: drena la cola local hacia Supabase.
      await this.syncToRemote();

      // Update last successful sync timestamp
      await this.setLastSyncTime();

      this.notifyListeners({ type: 'sync_completed', phase: 'push' });
  }

  /**
   * PULL de todas las tablas (animales + empresa-scoped).
   *
   * Delega la lógica de fetch incremental al módulo puro `syncPull` (única
   * fuente de catch-up, compartida con realtime). El módulo calcula el cursor
   * por empresa (`getLastByDateForEmpresa`) y trae solo lo nuevo desde
   * `updated_at`; si el caché local está vacío para la empresa, el cursor es
   * epoch y trae todo (carga inicial).
   *
   * @param force si true, ignora el guard de realtime y pullea todas las
   *   tablas (uso en la carga inicial: los datos deben estar disponibles al
   *   arrancar, y realtime podría ya estar "activo" sin haber entregado aún).
   *   Si false, salta las tablas cubiertas por realtime (PULL periódico
   *   redundante cuando el canal ya las mantiene actualizadas).
   */
  private async pullAllTables(force = false): Promise<void> {
    let empresaId = await this.getActiveEmpresaId();

    // Carrera del primer login: AuthContext escribe el profile local de forma
    // async (loadProfileFromSupabase sin await) DESPUÉS de setUser, así que el
    // efecto [user] de SyncContext puede disparar init() antes de que el
    // profile aterrice en IndexedDB → empresaId ''. Pullear con scope vacío
    // manda .eq('empresa_id', '') → 400 22P02 por tabla. Esperar un rato a que
    // la escritura in-flight termine (mismo patrón que ensureRealtimeForUser
    // en SyncContext); si no llega, omitir el PULL — la safety net de realtime
    // (evento local de `profiles`) y el sync periódico hacen el catch-up.
    if (!empresaId) {
      empresaId = await this.waitForActiveEmpresa();
    }
    if (!empresaId) {
      console.warn('[SyncManager] pullAllTables sin empresa activa (profile aún no cargado): PULL omitido');
      return;
    }

    const configs = this.buildPullConfigs();

    // Separar tablas a pullear de las cubiertas por realtime (para logging).
    // Con force=true (arranque) se pullean todas. Con force=false (periódico)
    // se saltan las que realtime ya mantiene al día — si realtime está caído,
    // isCoveredByRealtime da false para todas y se pullea todo.
    const pull: string[] = [];
    const skipped: string[] = [];
    for (const c of configs) {
      if (force || !this.isCoveredByRealtime(c.table)) pull.push(c.table);
      else skipped.push(c.table);
    }

    console.log(
      `[SyncManager] pullAllTables(force=${force}, realtime=${this.isRealtimeActive()}): ` +
      `PULL [${pull.join(', ')}]` +
      (skipped.length ? ` | skip por realtime [${skipped.join(', ')}]` : '')
    );

    await pullTables(this.supabase, configs, empresaId, pull);
  }

  /**
   * empresaId activa del usuario actual. try/catch: si no hay sesión/profile,
   * retorna '' (string vacío) → `syncPull` usa cursor epoch / salta scope.
   * Centraliza el patrón que antes estaba duplicado en syncToRemote /
   * getLastLocalDate / getLastLocalAnimalUpdate.
   */
  private async getActiveEmpresaId(): Promise<string> {
    try {
      const { profile } = getRepositories();
      const userInfo = await profile.getCurrentUserInfo();
      return userInfo?.empresaId || '';
    } catch {
      return '';
    }
  }

  /**
   * Espera acotada a que el profile local aterrice (carrera del primer login,
   * ver pullAllTables). La escritura de AuthContext/authService está in-flight
   * y normalmente resuelve en ~un roundtrip, así que un polling corto alcanza.
   * Corte temprano si el profile ya existe pero no tiene empresa activa (no
   * hay nada que esperar) y si se agota el deadline sin profile (usuario sin
   * sesión/empresa: el PULL se omite y el periodic/realtime retoma después).
   */
  private async waitForActiveEmpresa(timeoutMs = 4000, intervalMs = 150): Promise<string> {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      try {
        const { profile } = getRepositories();
        const info = await profile.getCurrentUserInfo();
        if (info.empresaId) return info.empresaId;
        if (info.userId) return ''; // profile cargado pero sin empresa activa
      } catch { /* factory no lista todavía: reintentar hasta el deadline */ }
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
    return '';
  }

  /**
   * ¿Está esta tabla cubierta por realtime?
   *  true  → el canal `empresa:{id}` está activo Y la tabla está registrada en
   *          TableRealtimeSync: sus cambios llegan por broadcast, el PULL
   *          periódico es redundante.
   *  false → realtime caído/no activo, o tabla sin realtime (ej: `empresas`):
   *          hay que pullearla.
   */
  private isCoveredByRealtime(table: string): boolean {
    if (!this.isRealtimeActive()) return false;
    return this.tableRealtimeSync?.isRegistered(table) ?? false;
  }

  async syncToRemote(): Promise<void> {
    // Offline → no push (offline-first, la cola queda para después).
    // Sin sesión válida → no pushear como anon (RLS rechazaría / "permission denied
    // for function ..."). Misma guarda que syncPending/GetTableData/syncInitialAnimales.
    // Cubre llamadas directas a syncToRemote que NO pasan por syncPending (ej: QueueViewer).
    if (!this.getAdapter().isOnline() || !(await this.hasValidSessionForRemote())) {
      console.log('[SyncManager] Skipping syncToRemote: offline or no valid session');
      return;
    }
    const adapter = this.getAdapter();
    const pending = await adapter.getPendingChanges();

    if (pending.length === 0) return;

    // Filtrar pendientes por empresa activa: solo enviar los que pertenecen a
    // la empresa actual. Los de otras empresas se quedan en la cola y se
    // enviarán cuando el usuario vuelva a esa empresa. Sin este filtro, los
    // pendientes de empresa A enviados mientras se está en empresa B serían
    // rechazados por RLS (empresa_id del record ≠ empresa_activa_id del JWT)
    // y quedarían en retry infinito.
    let activeEmpresaId: string | null = null;
    try {
      const { profile } = getRepositories();
      const userInfo = await profile.getCurrentUserInfo();
      activeEmpresaId = userInfo.empresaId || null;
    } catch { /* ignore */ }

    const activePending = activeEmpresaId
      ? pending.filter(change => {
          const { action, record } = change;
          // DELETE / BULK_DELETE: record es solo IDs (strings), no hay
          // empresa_id para filtrar. Los enviamos y dejamos que RLS decida.
          if (action === 'DELETE' || action === 'BULK_DELETE') return true;
          // INSERT / UPDATE: record es un objeto con empresa_id
          if (record && typeof record === 'object' && !Array.isArray(record)) {
            const emp = (record as Record<string, any>).empresa_id;
            // Sin empresa_id (tablas globales como empresas, profiles) → enviar
            if (!emp) return true;
            return emp === activeEmpresaId;
          }
          // BULK_INSERT / BULK_UPDATE: array de objetos, asumimos misma empresa
          if (Array.isArray(record) && record.length > 0 && typeof record[0] === 'object') {
            const emp = (record[0] as Record<string, any>).empresa_id;
            if (!emp) return true;
            return emp === activeEmpresaId;
          }
          // Formato desconocido → no filtrar
          return true;
        })
      : pending;

    const skippedCount = pending.length - activePending.length;
    if (skippedCount > 0) {
      console.log(
        `[SyncManager] ${skippedCount} pending change(s) de otra empresa retenidos ` +
        `(se enviarán cuando se vuelva a esa empresa). Enviando solo empresa activa.`
      );
    }
    if (activePending.length === 0) {
      console.log('[SyncManager] No hay pendientes para la empresa activa, skip push');
      return;
    }

    // Cap de reintentos (mismo constante que el SW): los pendientes que
    // agotaron MAX_PUSH_RETRIES quedan RETENIDOS en la cola como CONFLICTO —
    // no se envían (evita retry infinito de errores determinísticos como
    // uniqueness) ni se borran (el dato sobrevive hasta intervención manual).
    // Los RECHAZADOS por permisos (RLS) tampoco se re-envían: el rechazo es
    // determinístico y ya fueron notificados al usuario.
    const exhausted = activePending.filter(esPendienteRetenido);
    const toProcess = activePending.filter(c => !esPendienteRetenido(c));
    if (exhausted.length > 0) {
      console.error(
        `[SyncManager] [SYNC-CONFLICT] ${exhausted.length} cambio(s) agotaron ` +
        `${MAX_PUSH_RETRIES} reintentos y quedan retenidos en la cola (no se borran):`,
        exhausted.map(c => ({ id: c.id, table: c.table, action: c.action, lastError: c.lastError })),
      );
    }
    if (toProcess.length === 0) {
      console.log('[SyncManager] Todos los pendientes activos son conflictos agotados, skip push');
      return;
    }

    // Lock compartido SW↔app: si el Service Worker está procesando la cola,
    // salir para no pisarse (mismo SyncDB.pending_changes). El SW libera al
    // terminar (o expira por TTL si crashea).
    const lockMeta: MetaTable = {
      get: () => adapter.getSyncMetadata(SYNC_LOCK_KEY),
      put: (payload) => adapter.setSyncMetadata(SYNC_LOCK_KEY, payload),
    };
    const acquired = await acquireSyncLock(lockMeta, 'app', SYNC_LOCK_TTL_MS);
    if (!acquired) {
      console.log('[SyncManager] Sync lock held by SW, skipping push');
      return;
    }

    try {
      // Senders inyectados: usan this.supabase y aplican stripLocalFields.
      const senders: BatchSenders = {
        upsertRecords: async (table, records) => {
          const cleanRecords = records.map(r => this.stripLocalFields(r, table));
          const { error } = await this.supabase.from(table).upsert(cleanRecords);
          if (error) {
            console.error(`[SyncManager] Batch upsert error on ${table}:`, error.message);
            return { ok: false, error: error.message };
          }
          return { ok: true };
        },
        deleteByIds: async (table, ids) => {
          const pk = TABLE_CONFIG[table]?.primaryKey || 'id';
          const { error } = await this.supabase.from(table).delete().in(pk, ids);
          if (error) {
            console.error(`[SyncManager] Batch delete error on ${table}:`, error.message);
            return { ok: false, error: error.message };
          }
          return { ok: true };
        },
        bulkUpdateRecords: async (table, records) => {
          const pk = TABLE_CONFIG[table]?.primaryKey || 'id';
          const cleanRecords = records.map(r => this.stripLocalFields(r, table));
          const { error } = await this.supabase
            .from(table)
            .upsert(cleanRecords, { onConflict: pk, defaultToNull: false });
          if (error) {
            console.error(`[SyncManager] Batch update error on ${table}:`, error.message);
            return { ok: false, error: error.message };
          }
          return { ok: true };
        },
      };

      const result = await flushPendingBatch(toProcess, senders, { batchSize: BULK_BATCH_SIZE });

      // Eliminar de la cola los pendientes exitosos (bulk-remove si existe).
      if (result.succeededPendingIds.length > 0) {
        if (adapter.removePendingChanges) {
          await adapter.removePendingChanges(result.succeededPendingIds);
        } else {
          for (const id of result.succeededPendingIds) {
            await adapter.removePendingChange(id);
          }
        }
      }

      // Marcar los fallidos para que el Background Sync reintente el lote, con
      // el error REAL del grupo (table:action). El reintento que agota el
      // máximo se marca CONFLICTO: identificación duradera en la fila para
      // intervención manual (el cambio NO se borra de la cola).
      // Excepción — permisos (RLS): el rechazo es determinístico, así que se
      // marca RECHAZADO en el primer intento (sin rampa de reintentos) y se
      // notifica a la UI para que el usuario sepa que su cambio no subió.
      const byId = new Map(toProcess.map(c => [c.id, c]));
      const rechazados: { table?: string; action?: string; error: string }[] = [];
      for (const id of result.failedPendingIds) {
        if (adapter.markPendingChange) {
          const change = byId.get(id);
          const groupError =
            (change && result.groupErrors?.[`${change.table}:${change.action}`]) || 'batch failed';
          const esRechazado = esErrorPermisos(groupError);
          const retriesAfterMark = (change?.retryCount || 0) + 1;
          await adapter.markPendingChange(
            id,
            esRechazado
              ? mensajeRechazadoPermisos(groupError)
              : retriesAfterMark >= MAX_PUSH_RETRIES
                ? `CONFLICTO: ${groupError}`
                : groupError,
          );
          if (esRechazado) {
            rechazados.push({ table: change?.table, action: change?.action, error: groupError });
          }
        }
      }

      if (rechazados.length > 0) {
        console.error(
          `[SyncManager] [SYNC-RECHAZADO] ${rechazados.length} cambio(s) rechazados por permisos ` +
            `(RLS) — retenidos en la cola sin reintentar, requieren intervención:`,
          rechazados,
        );
        // Notificación a la UI (el panel de sync escucha este evento). El SW
        // tiene su propio path y no pasa por acá (no tiene window).
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('mcd:sync-rechazado', { detail: { cantidad: rechazados.length } }),
          );
        }
      }
    } finally {
      await releaseSyncLock(lockMeta, 'app');
    }
  }

  /**
   * Divide un array en chunks de tamaño `size`.
   * Helper para operaciones bulk con chunking.
   */
  private chunkArray<T>(arr: T[], size: number): T[][] {
    if (size <= 0 || arr.length === 0) return [arr];
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  }

  /**
   * Verifica si hay una sesión válida para hacer queries remotas a Supabase.
   * - Sin sesión → false (la query iría como `anon` y las políticas RLS la rechazan
   *   → "permission denied for function has_module_permission / check_user_in_empresa / ...").
   * - Token por vencer o vencido → refresca antes de continuar. Evita la race en la que la
   *   primer query del sync sale con token vencido y Supabase la trata como anon hasta que
   *   `autoRefreshToken` reacciona (típico en mobile, donde el JWT persistido puede estar
   *   vencido al arrancar).
   *
   * Debe llamarse DESPUÉS del check de offline: si no hay red, no hay query remota posible
   * (y refreshSession fallaría). Offline-first: sin red la app usa datos locales directamente.
   */
  private async hasValidSessionForRemote(): Promise<boolean> {
    try {
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session) {
        console.log('[SyncManager] Skip remote sync: no session');
        return false;
      }
      const expiresAtMs = (session.expires_at ?? 0) * 1000;
      if (expiresAtMs && expiresAtMs < Date.now() + 60_000) {
        const { error } = await this.supabase.auth.refreshSession();
        if (error) {
          console.log('[SyncManager] Skip remote sync: token refresh failed -', error.message);
          return false;
        }
      }
      return true;
    } catch (e) {
      console.log('[SyncManager] Skip remote sync: session check failed -', e);
      return false;
    }
  }

  /**
   * Rebuild all stocks from history.
   *
   * Sin clear(): recalcularStock hace upsert por stock_id (stockRepo.put,
   * que crea la fila si falta). El clear() anterior destruía los stocks de
   * TODAS las empresas (incluyendo las que siguen en caché) y sólo
   * reconstruía los de la empresa activa (los únicos cuyos stocks_historico
   * quedaron tras el fetch RLS-filtered).
   */
  async reconstruirTodosLosStocks(): Promise<void> {
    const stockHistoricoRepo = this.getRepo('stocks_historico');
    const stockRepo = this.getRepo('stocks');

    if (!stockHistoricoRepo || !stockRepo) return;

    const todosLosRegistros = await stockHistoricoRepo.getAll();
    const stockIds = [...new Set(todosLosRegistros.map(r => r.stock_id))];

    for (const stockId of stockIds) {
      await this.recalcularStock(stockId);
    }
  }

  /**
   * Recalculate current state of a stock from its history
   */
  async recalcularStock(stockId: string): Promise<void> {
    if (!isFactoryReady()) return;

    const stockHistoricoRepo = getRepositories().stockHistorico;
    const stockRepo = getRepositories().stock;

    const historial = await stockHistoricoRepo.getByStockId(stockId);

    console.log(`Recalculating stock for stock_id: ${stockId} with ${historial.length} historical records.`);
    if (historial.length === 0) {
      await stockRepo.delete(stockId);
      return;
    }

    // Sort by fecha_evento, then by updated_at
    historial.sort((a, b) => {
      if (a.fecha_evento < b.fecha_evento) return -1;
      if (a.fecha_evento > b.fecha_evento) return 1;
      return (a.updated_at || '').localeCompare(b.updated_at || '');
    });

    // Calculate current quantity
    let cantidadActual = 0;
    for (const operacion of historial) {
      if (operacion.tipo_operacion === 'COMPRA') {
        cantidadActual += operacion.cantidad_operacion;
      } else if (operacion.is_deleted === false) {
        cantidadActual -= Math.abs(operacion.cantidad_operacion);
      }
    }

    const ultimoRegistro = historial[historial.length - 1];
    const primeraCompra = historial.find(h => h.tipo_operacion === 'COMPRA' && !h.is_deleted);
    const unidadMedida = primeraCompra?.unidad_medida || 'unidad';

    const stock: any = {
      id: stockId,
      nombre: ultimoRegistro.nombre,
      categoria: ultimoRegistro.categoria,
      unidad_medida: unidadMedida,
      cantidad: cantidadActual,
      updated_at: new Date().toISOString(),
      fecha_evento: ultimoRegistro.fecha_evento,
      fecha_creacion: historial[0].updated_at,
      empresa_id: ultimoRegistro.empresa_id,
      // El link a concepto vive SOLO en los registros COMPRA (lo escribe
      // inventarioService.registrarCompra; CONSUMO/VENTA no lo llevan), así que
      // se deriva de primeraCompra igual que unidad_medida — no de
      // ultimoRegistro, que tras un consumo vendría undefined y put-replace
      // lo pisaría en cada reconstrucción.
      concepto_id: primeraCompra?.concepto_id
    };

    // put (upsert), no update: en un caché fresco la fila no existe y
    // Table.update sería NO-OP → el inventario nunca se reconstruía.
    await stockRepo.put(stock);
  }

  /**
   * ¿Está la pestaña oculta (tab en background / minimizada)?
   *
   * Cuando el usuario deja el browser inactivo, el navegador suspende
   * conexiones inactivas y el canal de realtime (WebSocket) suele caer o
   * throttlearse. Eso hace que `isCoveredByRealtime()` devuelva false para
   * TODAS las tablas, y el sync periódico empiece a pullear todo de Supabase
   * en cada tick — un gasto innecesario de llamadas mientras nadie mira.
   *
   * Gatear el sync periódico con la Page Visibility API evita eso: si la
   * pestaña no está visible, no hay motivo de sincronizar (realtime sigue
   * entregando server→cliente mientras pueda, y el PUSH solo drena pendientes
   * que el usuario genera mientras está activo). Al volver a la pestaña se
   * dispara un catch-up (ver `onVisibilityChange`).
   */
  private isTabHidden(): boolean {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
  }

  startPeriodicSync(interval = 300000, runImmediately = true): void {
    if (this.syncTimer) {
      console.log("Sync loop is already active.");
      return;
    }
    console.log("Starting periodic sync loop...");

    const loop = async () => {
      try {
        const adapter = this.getAdapter();
        // Pestaña oculta → saltamos: el usuario no está activo y no aporta
        // valor llamar a Supabase. El catch-up corre al volver a la pestaña.
        if (adapter.isOnline() && !this.isTabHidden()) {
          await this.syncPending();
        } else if (this.isTabHidden()) {
          console.log('[SyncManager] Pestaña oculta — sync periódico saltado');
        }
      } catch (error) {
        console.error("Error during periodic sync:", error);
      } finally {
        this.syncTimer = setTimeout(loop, interval);
      }
    };

    // Catch-up: al volver a la pestaña visible sincronizamos de inmediato para
    // reflejar los cambios que llegaron (por realtime o que se postergaron)
    // mientras el usuario estuvo fuera. Además reconectamos realtime si el
    // navegador lo suspendió/cerró por inactividad.
    if (typeof document !== 'undefined') {
      this.onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          const adapter = this.getAdapter();
          if (adapter.isOnline()) {
            console.log('[SyncManager] Pestaña visible — catch-up sync');
            this.syncPending().catch(err => console.error('Catch-up sync error:', err));
            this.ensureRealtimeConnected();
          }
        }
      };
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    // Run immediately by default (restores original behavior)
    if (runImmediately) {
      loop();
    } else {
      // Schedule the first run after the interval
      this.syncTimer = setTimeout(loop, interval);
    }
  }

  stopPeriodicSync(): void {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
      console.log("Periodic sync loop stopped.");
    }
    if (this.onVisibilityChange && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.onVisibilityChange = null;
    }
  }

  /**
   * Initialize the manager: load data, subscribe to events, and start the loop.
   */
  async init(): Promise<void> {
    if (this.initialized) {
      return;
    }

    console.log("Initializing SyncManager...");

    // Recovery: reset any stuck sync state from previous run (e.g., app crash during sync)
    this.forceResetSyncState();

    this.initialized = true;

    try {
      this.notifyListeners({ type: 'sync_started', phase: 'initial_load' });

      // Carga inicial FORZADA: pullea todas las tablas sin importar realtime.
      // El guard de realtime NO aplica acá porque al arranque los datos deben
      // estar disponibles (realtime puede estar "activo" pero aún sin entregar
      // el catch-up, y la UI/tests esperan los datos ya cargados). El PULL es
      // idempotente (cursor updated_at), así que no duplica con el catch-up.
      await this.pullAllTables(true);

      // Start periodic sync loop SIN runImmediately: la carga inicial ya se
      // hizo arriba; el primer tick del periódico es a los 5 min (donde sí
      // aplica el guard de realtime → salta el PULL si realtime está activo).
      this.startPeriodicSync(300000, false);

      this.notifyListeners({ type: 'sync_completed', phase: 'initial_load' });

      console.log("✅ SyncManager initialized correctly.");

    } catch (error) {
      console.error("❌ SyncManager initialization failed:", error);
      this.initialized = false;
    }
  }

  /**
   * Recarga de datos tras cambiar de empresa activa.
   *
   * Por qué existe: la RLS filtra por `empresa_activa_id`, así que al cambiar de
   * empresa las queries a Supabase devuelven SOLO los datos de la nueva empresa.
   * Pero `init()` es no-op después del primer login (flag `initialized`), y el
   * sync incremental (`gt('updated_at', lastLocal)`) no traería los registros de
   * la nueva empresa si su `updated_at` es anterior al último registro local de
   * la empresa anterior.
   *
   * Ahora delega al módulo puro `syncPull` (misma función que el PULL periódico y
   * el catch-up de realtime). El cursor es por empresa (`getLastByDateForEmpresa`):
   * si el caché local no tiene datos para la empresa destino, el cursor es epoch
   * y se trae todo (carga completa); si ya tiene, trae solo lo nuevo. Así se
   * cubre tanto el primer acceso a una empresa como el switch back, sin lógica
   * especial por tabla. El hook `afterCatchUp` de `stocks_historico` dispara la
   * reconstrucción de `stocks` (tabla derivada).
   *
   * Offline: si no hay conexión no se puede recargar; la UI mostrará la empresa
   * vacía hasta volver a estar online.
   */
  async reloadAfterCompanySwitch(empresaId: string): Promise<void> {
    console.log('[SyncManager] 🔄 Recargando datos tras cambio de empresa:', empresaId);

    if (!empresaId) {
      console.warn('[SyncManager] reloadAfterCompanySwitch sin empresaId, aborta');
      return;
    }

    if (!this.getAdapter().isOnline()) {
      console.log('[SyncManager] ⚠️ Offline: no se recarga tras cambio de empresa');
      return;
    }

    try {
      this.notifyListeners({ type: 'sync_started', phase: 'company_switch' });

      // PULL de todas las tablas configuradas para la nueva empresa. El cursor
      // por empresa decide full vs incremental automáticamente. La reconstrucción
      // de stocks la dispara el afterCatchUp de stocks_historico.
      // Nota: la reconexión del realtime la maneja SyncContext al detectar el
      // cambio de activeEmpresaId (es el único dueño del ciclo de vida del realtime).
      await pullTables(this.supabase, this.buildPullConfigs(), empresaId);

      this.notifyListeners({ type: 'sync_completed', phase: 'company_switch' });
      console.log('[SyncManager] ✅ Recarga tras cambio de empresa completa');
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('[SyncManager] ❌ Error recargando tras cambio de empresa:', error);
      this.notifyListeners({ type: 'sync_error', error: msg });
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.stopPeriodicSync();
    if (this.networkUnsubscribe) {
      this.networkUnsubscribe();
      this.networkUnsubscribe = null;
    }
    if (this.repositoryEventsUnsubscribe) {
      this.repositoryEventsUnsubscribe();
      this.repositoryEventsUnsubscribe = null;
    }
    this.initialized = false;
  }

  // ==================== Realtime Sync (genérico, multi-tabla) ====================

  /**
   * Configuración de tablas que sincronizan por realtime. Cada entrada
   * corresponde a una tabla con trigger de broadcast en Supabase.
   *
   * Fase 1+2: `animales` (ya tenía su propio trigger) + `gastos` (piloto).
   * El resto de tablas empresa-scoped se suma al confirmar el flujo E2E
   * (ver docs/REALTIME_GENERICO_TODAS_TABLAS.md, Fase 4).
   */
  private buildPullConfigs(): PullTableConfig[] {
    return [
      // animales: PK compuesta (empresa_id, caravana); soft-delete lógico.
      { table: 'animales', repoKey: 'animal', pkField: 'caravana', isDeletedLogic: true },

      // empresas: caso especial. NO tiene empresa_id (ella ES la empresa, su PK
      // id = empresa_id). Se scopea por 'id' y emite por trigger propio
      // (broadcast_empresa_self_change). No usa is_deleted.
      { table: 'empresas', repoKey: 'empresa', pkField: 'id', isDeletedLogic: false, scopeField: 'id' },

      // --- Tablas con soft-delete (is_deleted en Supabase) ---
      { table: 'gastos', repoKey: 'gasto', pkField: 'id', isDeletedLogic: true },
      { table: 'ingresos', repoKey: 'ingreso', pkField: 'id', isDeletedLogic: true },
      { table: 'eventos', repoKey: 'evento', pkField: 'id', isDeletedLogic: true },
      { table: 'notas', repoKey: 'nota', pkField: 'id', isDeletedLogic: true },
      // Notas ancladas (spec 2026-10-16): anclas + personal del campo, mismo
      // modelo que notas (soft-delete; broadcast empresa:{id} → realtime).
      { table: 'nota_anclas', repoKey: 'notaAncla', pkField: 'id', isDeletedLogic: true },
      { table: 'personas_campo', repoKey: 'personaCampo', pkField: 'id', isDeletedLogic: true },
      // Apodos (2026-10-18): SÓLO LECTURA en la web (autocompletar de la contraparte). Sin
      // trigger de broadcast → realtime:false (pull periódico). Mobile no registra el
      // repo → el pull lo saltea. No está en TABLE_MAPPING: nunca se pushea.
      { table: 'apodos', repoKey: 'apodo', pkField: 'id', isDeletedLogic: true, realtime: false },
      { table: 'recordatorios', repoKey: 'recordatorio', pkField: 'id', isDeletedLogic: true },
      { table: 'lluvias', repoKey: 'lluvia', pkField: 'id', isDeletedLogic: true },
      { table: 'elementos_mapa', repoKey: 'elementoMapa', pkField: 'id', isDeletedLogic: true },
      // Puntos de control del mapa: puntos fijos + observaciones (soft-delete;
      // triggers de broadcast en el canal empresa:{id} → realtime activo).
      { table: 'puntos_control', repoKey: 'puntoControl', pkField: 'id', isDeletedLogic: true },
      { table: 'puntos_control_eventos', repoKey: 'puntoControlEvento', pkField: 'id', isDeletedLogic: true },
      // Lotes de siembra: siembras de cada lote (soft-delete; triggers de broadcast en el
      // canal empresa:{id} → realtime activo). Mobile no registra el repo (no sincroniza esta
      // tabla en esta fase): ahí getPullRepo da null y el pull/catch-up no aplica nada
      // ("Repo 'siembraLote' no disponible"), igual que con `apodos`.
      { table: 'siembras_lote', repoKey: 'siembraLote', pkField: 'id', isDeletedLogic: true },
      { table: 'sigsa_declaraciones', repoKey: 'sigsaDeclaracion', pkField: 'id', isDeletedLogic: true },
      // Cola de acciones server-side: sin soft-delete (append-only, estados
      // terminales protegidos por trigger SQL). FULL SNAPSHOT (tabla chica):
      // el cursor incremental por updated_at no sirve acá — la fila que el
      // cliente acaba de pushear se ejecuta en el server milisegundos después
      // (trigger AFTER INSERT), y con cualquier skew de reloj entre cliente y
      // server el updated_at de la fila ejecutada queda <= al cursor local y
      // el pull incremental jamás la traería (la copia local quedaría en
      // 'pendiente' para siempre aunque ya se ejecutó).
      { table: 'sync_acciones', repoKey: 'syncAcciones', pkField: 'id', isDeletedLogic: false,
        fullSnapshot: true, realtime: false, getPendingChanges: () => this.getPendingChangesSafe() },
      { table: 'operacion_compra_venta_animales', repoKey: 'operacion', pkField: 'id', isDeletedLogic: true },
      { table: 'stocks_historico', repoKey: 'stockHistorico', pkField: 'id', isDeletedLogic: true,
        // stocks_historico es la fuente de `stocks` (derivada): tras el catch-up
        // hay que recalcular los stocks afectados para que la UI de stock refleje
        // el cambio. (syncTableDataHistorico hacía esto vía updateLocalStocks;
        // con realtime activo, ese PULL se salta, así que lo cubre este hook.)
        afterCatchUp: async () => { await this.reconstruirTodosLosStocks(); } },

      // --- Tablas SIN soft-delete (hard delete; DELETE resuelto por evento) ---
      { table: 'unidades', repoKey: 'unidad', pkField: 'id', isDeletedLogic: true },
      { table: 'ubicaciones', repoKey: 'ubicacion', pkField: 'id', isDeletedLogic: true },

      // --- Taxonomía de conceptos (spec 2026-08-07) ---
      // Las 4 van por FULL SNAPSHOT (tablas chicas: ~63 filas de seed + propios
      // + relaciones): `conceptos_ocultos`/`conceptos_confirmados` no tienen
      // updated_at (sin cursor posible) y el snapshot además reconcilea
      // hard-deletes remotos (p.ej. un desocultar desde otro dispositivo) que
      // un pull por cursor nunca vería.
      // categorias/conceptos van con scopeField=null (SIN filtro de empresa):
      // los globales tienen empresa_id NULL y el RLS del SELECT ya devuelve
      // exactamente globales + propios.
      // realtime: false → no tienen trigger de broadcast en el canal
      // empresa:{id}; si se registraran, isCoveredByRealtime() daría true y el
      // PULL periódico las saltearía esperando realtime que nunca llega.
      ...this.buildTaxonomiaPullConfigs(),
    ];
  }

  /**
   * Configs de PULL de la taxonomía. Los sub-repos de las tablas de relación
   * (ocultos/confirmados) se resuelven vía el ConceptoRepository registrado;
   * si la factory aún no está lista (arranque temprano), esos configs quedan
   * sin repo inline y el pull los salta hasta el próximo ciclo.
   */
  private buildTaxonomiaPullConfigs(): PullTableConfig[] {
    let relacion: { ocultos?: ConceptoRelacionRepository; confirmados?: ConceptoRelacionRepository } = {};
    try {
      if (isFactoryReady()) {
        const { concepto } = getRepositories() as any;
        if (concepto?.ocultosRepo) {
          relacion = { ocultos: concepto.ocultosRepo, confirmados: concepto.confirmadosRepo };
        }
      }
    } catch {
      // factory no lista → configs sin repo inline (el pull los saltea)
    }
    // Scope del reconcile para las tablas globales+propias: una fila local con
    // empresa_id de OTRA empresa no es "ausencia remota" (el snapshot de la
    // empresa activa — globales + propias vía RLS — jamás la devolvería), así
    // que el reconcile no puede borrarla del caché. Sin esto, al cambiar de
    // empresa el pull periódico borraba los conceptos de la empresa anterior.
    const scopeTaxonomia = (row: any, empresaId: string): boolean =>
      row?.empresa_id == null || row.empresa_id === empresaId;
    return [
      { table: 'categorias', repoKey: 'categoria', fullSnapshot: true, scopeField: null, scopeFilter: scopeTaxonomia, realtime: false, getPendingChanges: () => this.getPendingChangesSafe() },
      { table: 'conceptos', repoKey: 'concepto', fullSnapshot: true, scopeField: null, scopeFilter: scopeTaxonomia, realtime: false, getPendingChanges: () => this.getPendingChangesSafe() },
      // Las tablas de relación son empresa-scoped puras (sin filas globales):
      // el guard por scopeField='empresa_id' del reconcile ya las cubre.
      { table: 'conceptos_ocultos', repo: relacion.ocultos, fullSnapshot: true, realtime: false, getPendingChanges: () => this.getPendingChangesSafe() },
      { table: 'conceptos_confirmados', repo: relacion.confirmados, fullSnapshot: true, realtime: false, getPendingChanges: () => this.getPendingChangesSafe() },
    ];
  }

  /**
   * Pendientes de push con fallback a lista vacía: lo consume el reconcile del
   * fullSnapshot (syncPull) para no borrar del caché filas creadas offline que
   * aún no llegaron al server. Nunca debe romper el pull por una cola ilegible.
   */
  private async getPendingChangesSafe(): Promise<PendingChange[]> {
    try {
      return await this.getAdapter().getPendingChanges();
    } catch {
      return [];
    }
  }

  /**
   * Inicializa el realtime (un canal `empresa:{id}` para todas las tablas
   * registradas). Idempotente y reentrante: si ya escucha la MISMA empresa no
   * hace nada; si cambió la empresa, reconecta al canal correcto.
   *
   * NO requerir isListening() aquí: la suscripción es async y pasa por
   * INITIAL_SYNC antes de SUBSCRIBED. Re-crear el canal en cada llamada mataba
   * el WebSocket en pleno handshake → TIMED_OUT. El retry interno del
   * TableRealtimeSync maneja errores transitorios. Único caso de reconexión
   * para la misma empresa: estado ERROR terminal (agotó reintentos).
   */
  async initRealtime(empresaId: string): Promise<void> {
    if (this.tableRealtimeSync && this.tableRealtimeSync.getEmpresaId() === empresaId) {
      const gaveUp = this.tableRealtimeSync.getState() === 'error';
      if (!gaveUp) {
        console.log('[SyncManager] Realtime ya inicializado para empresa', empresaId, '(estado:', this.tableRealtimeSync.getState() + ') - omitiendo');
        return;
      }
      console.log('[SyncManager] Realtime en estado ERROR, reintentando para empresa', empresaId);
    }

    if (this.tableRealtimeSync) {
      const previous = this.tableRealtimeSync.getEmpresaId();
      console.log('[SyncManager] Re-conectando realtime', previous ? `(empresa ${previous} -> ${empresaId})` : '(no estaba escuchando)');
      await this.disconnectRealtime();
    }

    console.log('[SyncManager] Inicializando realtime para empresa:', empresaId);
    this.tableRealtimeSync = new TableRealtimeSync(this.supabase);
    // Solo tablas con trigger de broadcast en el canal empresa:{id}: las que
    // marcan realtime:false (ej. taxonomía de conceptos) no reciben eventos y
    // registrarlas haría que el PULL periódico las saltee (isCoveredByRealtime).
    this.buildPullConfigs()
      .filter(cfg => cfg.realtime !== false)
      .forEach(cfg => this.tableRealtimeSync!.register(cfg));
    await this.tableRealtimeSync.initialize(empresaId);

    console.log('[SyncManager] ✅ Realtime inicializado');
  }

  /**
   * Desconecta el realtime.
   */
  async disconnectRealtime(): Promise<void> {
    if (this.tableRealtimeSync) {
      console.log('[SyncManager] Desconectando realtime...');
      await this.tableRealtimeSync.disconnect();
      this.tableRealtimeSync = null;
      console.log('[SyncManager] ✅ Realtime desconectado');
    }
  }

  /**
   * Obtiene la instancia de TableRealtimeSync (para acceso a listeners, etc.)
   */
  getRealtime(): TableRealtimeSync | null {
    return this.tableRealtimeSync;
  }

  /**
   * Notifica cambios de red al realtime.
   */
  notifyNetworkChangeToRealtime(online: boolean): void {
    if (this.tableRealtimeSync) {
      this.tableRealtimeSync.onNetworkChange(online);
    }
  }

  /**
   * Verifica si el realtime está activo (escuchando).
   */
  isRealtimeActive(): boolean {
    return this.tableRealtimeSync !== null && this.tableRealtimeSync.isListening();
  }

  /**
   * Asegura que el canal de realtime esté escuchando. Si el navegador cerró/
   * suspendió el WebSocket por inactividad de la pestaña, esto lo reconecta.
   * Idempotente: si ya escucha, no hace nada. Pensado para el catch-up al
   * volver a una pestaña visible (visibilitychange).
   */
  private ensureRealtimeConnected(): void {
    if (!this.tableRealtimeSync) return;
    if (this.isRealtimeActive()) return;
    const empresaId = this.tableRealtimeSync.getEmpresaId();
    if (!empresaId) return;
    console.log('[SyncManager] Realtime inactivo al volver a la pestaña — reconectando');
    this.tableRealtimeSync.ensureConnected();
  }

  // ==================== Auth Session Methods ====================

  /**
   * Guarda la sesión de autenticación (para background sync)
   * Usa sync_metadata con key 'authSession'
   *
   * @param session - Sesión de Supabase a guardar
   */
  async setAuthSession(session: any): Promise<void> {
    try {
      const adapter = this.getAdapter();
      await adapter.setAuthSession(session);
      console.log('[SyncManager] Auth session saved');
    } catch (error) {
      console.warn('[SyncManager] Could not save auth session:', error);
    }
  }

  /**
   * Recupera la sesión de autenticación guardada
   *
   * @returns Sesión guardada o null
   */
  async getAuthSession(): Promise<any> {
    try {
      const adapter = this.getAdapter();
      return await adapter.getAuthSession();
    } catch (error) {
      console.warn('[SyncManager] Could not get auth session:', error);
      return null;
    }
  }

  /**
   * Limpia la sesión de autenticación guardada
   */
  async clearAuthSession(): Promise<void> {
    try {
      const adapter = this.getAdapter();
      await adapter.setAuthSession(null);
      console.log('[SyncManager] Auth session cleared');
    } catch (error) {
      console.warn('[SyncManager] Could not clear auth session:', error);
    }
  }
}
